/**
 * How a policy is laid out. Defaults (policyDocumentStyle) mirror what consumers
 * and venue owners see today, so every screen that shares this component keeps
 * its current look.
 */
export type PolicyDocumentStyle = {
  /** accordion = collapsible sections; open = every section always expanded */
  readonly layout: "accordion" | "open";
  /** toggle = single "Expand All"/"Collapse All" link; split = both links */
  readonly expandControls: "toggle" | "split";
  /** gradient = green→blue (privacy) / grey box; tinted = accent-50 box; none = no meta box */
  readonly footer: "gradient" | "tinted" | "none";
  /** Extra top margin before the footer box */
  readonly footerGap: boolean;
  /** Binding-agreement sentence shown in the footer */
  readonly footerNote?: string;
};

export const LEGAL_DOCUMENTS: ReadonlySet<string> = new Set([
  "terms_and_condition",
  "privacy_policy",
  "terms_and_condition_venue",
  "privacy_policy_venue",
]);

/** The style each policy has in the consumer / venue app where users agree to it. */
export function policyDocumentStyle(policyName: string): PolicyDocumentStyle {
  switch (policyName) {
    case "terms_and_condition":
      return {
        layout: "accordion",
        expandControls: "split",
        footer: "tinted",
        footerGap: true,
        footerNote:
          "By continuing to use DwellDrop, you acknowledge that you have read, understood, and agree to be bound by the Terms of Use.",
      };
    case "privacy_policy":
      return { layout: "accordion", expandControls: "split", footer: "gradient", footerGap: false };
    case "terms_and_condition_venue":
      return {
        layout: "open",
        expandControls: "toggle",
        footer: "tinted",
        footerGap: false,
        footerNote:
          "By continuing to use VenueHub, you acknowledge that you have read, understood, and agree to be bound by the Standard Merchant Terms.",
      };
    case "privacy_policy_venue":
      return { layout: "open", expandControls: "toggle", footer: "tinted", footerGap: false };
    default:
      return { layout: "accordion", expandControls: "toggle", footer: "gradient", footerGap: true };
  }
}
